import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmuListInputComponent } from '../../../../../shared/components/inputs/emu-list-input/emu-list-input.component';
import { MatIconModule } from '@angular/material/icon';
import { DashboardDto } from '../../../../../../api-main';
import { DashboardService } from '../../../services/dashboard.service';
import { DashboardTemplateService } from '../../../services/dashboard-template.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDeleteDialogComponent } from '../../../../../shared/components/dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { AlertService } from '../../../../../shared/services/alert.service';
import { WaitService } from '../../../../../shared/services/wait.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, finalize, map, take } from 'rxjs';

@Component({
  selector: 'app-dashboard-templates-select',
  standalone: true,
  imports: [CommonModule, EmuListInputComponent, MatIconModule],
  templateUrl: './dashboard-templates-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard-templates-select.component.scss',
})
export class DashboardTemplatesSelectComponent {
  private dashboardService = inject(DashboardService);
  private dashboardTemplateService = inject(DashboardTemplateService);
  private dialog = inject(MatDialog);
  private alertService = inject(AlertService);
  waitService = inject(WaitService);
  private translate = inject(TranslateService);

  templates$ = this.dashboardTemplateService.list();

  public inputPlaceholder = new BehaviorSubject<string>('');

  constructor() {
    this.checkForTemplates();
  }

  templateSelected(selectedDashboard: DashboardDto) {
    this.waitService.start();
    this.dashboardTemplateService
      .get(selectedDashboard.id)
      .pipe(finalize(() => this.waitService.stop()))
      .subscribe({
        next: (dashboard) => {
          dashboard.id = null;
          this.dashboardService.editMode$.next(true);
          this.dashboardService.dashboard = {
            ...dashboard,
            name: this.dashboardService.dashboard.name,
            id: this.dashboardService.dashboard.id,
          };
          this.dashboardService.items = dashboard.config;
          this.dashboardService.changedOptions();
        },
        error: () => this.alertService.error(this.translate.instant('TEMPLATE.SELECT_ERROR')),
      });
  }

  deleteTemplate(id: number) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { id: 0 };
    this.dialog
      .open(ConfirmDeleteDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) return;

        this.waitService.start();
        this.dashboardTemplateService
          .delete(id)
          .pipe(finalize(() => this.waitService.stop()))
          .subscribe({
            next: () =>
              this.alertService.info(this.translate.instant('DASHBOARD.TEMPLATE_DELETED')),
            error: () => this.alertService.error(this.translate.instant('TEMPLATE.DELETE_ERROR')),
            complete: () => {
              this.templates$ = this.dashboardTemplateService.list();
              this.checkForTemplates();
            },
          });
      });
  }

  checkForTemplates() {
    this.templates$
      .pipe(
        map((templates) =>
          templates.length > 0
            ? this.translate.instant('DASHBOARD.TEMPLATE_SELECT')
            : this.translate.instant('DASHBOARD.TEMPLATE_EMPTY'),
        ),
      )
      .subscribe((placeholder) => {
        this.inputPlaceholder.next(placeholder);
      });
  }
}
